import { Injectable, inject } from '@angular/core';
import { Observable, map, catchError, of, switchMap, forkJoin } from 'rxjs';
import { Apollo, gql } from 'apollo-angular';
import { ApolloQueryResult } from '@apollo/client';
import { Tir } from '../bo/tir';
import { Equipment } from '../bo/equipment';
import { Operator } from '../bo/operator';
import { BlastDrillingData, BlastRow, DrillSessionApiDto, DrillingSession, ShiftConfig } from 'src/app/shared/models/drilling.models';
import { HttpService } from './http.service';

const BLAST_KPI_QUERY = gql`
  query BlastKpi($id: Int!) {
    Blast(BlastId: $id) {
      Hole_Count
      BlastDesign {
        Geometry {
          Design {
            length
          }
        }
      }
    }
  }
`;

interface BlastKpiResult {
  Blast: {
    Hole_Count: number;
    BlastDesign: Array<{
      Geometry: {
        Design: { length: number } | null;
      } | null;
    }>;
  } | null;
}

export interface WeekBounds {
  start: Date;
  end: Date;
}

// export const DEFAULT_SHIFT_CONFIG: ShiftConfig = {
//   shiftsPerDay: 2,
//   shiftDurationHours: 12,
//   shiftLabels: ['day', 'night'],
//   shiftStartHours: [0, 12],
// };
export const DEFAULT_SHIFT_CONFIG: ShiftConfig = {
  shiftsPerDay: 3,
  shiftDurationHours: 8,
  shiftLabels: ['morning','day', 'night'],
  shiftStartHours: [6, 14, 22],
};

@Injectable({ providedIn: 'root' })
export class DrillActivityService {
  private readonly http = inject(HttpService);
  private readonly apollo = inject(Apollo);

  /** Returns Monday 00:00:00 and Sunday 23:59:59 of the ISO week containing today. */
  getCurrentWeekBounds(): WeekBounds {
    const today = new Date();
    const dow = today.getDay(); // 0 = Sun
    const daysToMonday = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(today);
    monday.setDate(today.getDate() + daysToMonday);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return { start: monday, end: sunday };
  }

  /**
   * Fetches blasts for a site that have at least one drill session within [bounds.start, bounds.end].
   * Uses a single session query to avoid N+1 requests, then fetches KPI per matched blast.
   */
  getBlastRowsForWeek(
    siteId: number,
    bounds: WeekBounds,
    config: ShiftConfig = DEFAULT_SHIFT_CONFIG,
  ): Observable<BlastRow[]> {
    const fromUtc = bounds.start.toISOString();
    const toUtc = bounds.end.toISOString();

    return forkJoin([
      this.http.get<Tir[]>(`Sites/${siteId}/Blasts`),
      this.fetchSessionsBySite(siteId, fromUtc, toUtc),
    ]).pipe(
      switchMap(([tirs, sessions]) => {
        const sessionsByBlast = new Map<number, DrillSessionApiDto[]>();
        for (const s of sessions) {
          const list = sessionsByBlast.get(s.blastId) ?? [];
          list.push(s);
          sessionsByBlast.set(s.blastId, list);
        }

        const activeTirs = tirs.filter(t => sessionsByBlast.has(t.tirId));
        if (activeTirs.length === 0) { return of([]); }

        return forkJoin(
          activeTirs.map(t =>
            this.fetchBlastKpi(t.tirId).pipe(
              map(kpi => ({
                ...this.mapTirToBlastRow(t),
                holeCount: kpi.holeCount,
                targetMeters: kpi.plannedLength,
                sessions: (sessionsByBlast.get(t.tirId) ?? []).map(dto => this.mapApiDtoToSession(dto, config)),
              })),
            ),
          ),
        );
      }),
    );
  }

  fetchEquipmentBySite(siteId: number): Observable<Equipment[]> {
    return this.http.get<Equipment[]>(`Sites/${siteId}/Equipment`)
      .pipe(catchError(() => of([] as Equipment[])));
  }

  fetchOperatorsBySite(siteId: number): Observable<Operator[]> {
    return this.http.get<Operator[]>(`Sites/${siteId}/Operators`)
      .pipe(catchError(() => of([] as Operator[])));
  }

  /** Fetches all drill sessions for a site whose start time falls within [fromUtc, toUtc]. */
  fetchSessionsBySite(siteId: number, fromUtc: string, toUtc: string): Observable<DrillSessionApiDto[]> {
    return this.http.get<DrillSessionApiDto[]>(
      `DrillSessions/site/${siteId}?fromDate=${fromUtc}&toDate=${toUtc}`,
    ).pipe(catchError(() => of([] as DrillSessionApiDto[])));
  }

  private mapTirToBlastRow(tir: Tir): BlastRow {
    return {
      blastId: tir.tirId,
      projectId: tir.projetId,
      label: tir.nom,
      zone: tir.nomZone ?? '',
      holeCount: 0,
      targetMeters: 0,
      blastDate: tir.dateTirPrevisionnelle
        ? this.toIsoDateString(tir.dateTirPrevisionnelle)
        : null,
      sessions: [],
    };
  }

  /** Fetches all drilling sessions for a blast from the API. */
  fetchSessionsByBlastId(blastId: number): Observable<DrillSessionApiDto[]> {
    return this.http.get<DrillSessionApiDto[]>(`DrillSessions/blast/${blastId}`);
  }

  /**
   * Fetches hole count and planned drilled length for a blast via GraphQL.
   * Planned length is the sum of each hole's design length from BlastDesign.
   * Returns { holeCount: 0, plannedLength: 0 } on error.
   */
  fetchBlastKpi(blastId: number): Observable<{ holeCount: number; plannedLength: number }> {
    return this.apollo
      .query<BlastKpiResult>({
        query: BLAST_KPI_QUERY,
        variables: { id: blastId },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result: ApolloQueryResult<BlastKpiResult>) => {
          const blast = result.data?.Blast;
          if (!blast) return { holeCount: 0, plannedLength: 0 };
          const holeCount = blast.Hole_Count ?? 0;
          const plannedLength = Math.round((blast.BlastDesign ?? []).reduce(
            (sum, hole) => sum + (hole.Geometry?.Design?.length ?? 0),
            0,
          ));
          return { holeCount, plannedLength };
        }),
        catchError(() => of({ holeCount: 0, plannedLength: 0 })),
      );
  }

  /** Maps a backend DrillSessionDto to the frontend DrillingSession view-model. */
  mapApiDtoToSession(dto: DrillSessionApiDto, config: ShiftConfig = DEFAULT_SHIFT_CONFIG): DrillingSession {
    const start = new Date(dto.sessionStartUtc);
    const end = dto.sessionEndUtc ? new Date(dto.sessionEndUtc) : null;
    const shiftDurationHours = end ? (end.getTime() - start.getTime()) / 3_600_000 : 0;
    // Use local hours/date so sessions align with locally-generated shift columns
    const shiftIndex = DrillActivityService.resolveShiftIndex(start.getHours(), config.shiftStartHours);
    const engineHoursStart = dto.engineHoursStart ?? 0;
    const engineHoursEnd = dto.engineHoursEnd ?? 0;
    const engineHours = Math.max(0, engineHoursEnd - engineHoursStart);

    return {
      id: dto.id,
      blastId: dto.blastId.toString(),
      equipmentId: dto.drillRigId ?? 0,
      equipmentLabel: dto.drillRigName ?? '',
      operatorId: dto.operatorId ?? 0,
      operatorLabel: dto.operatorName ?? '',
      date: this.toIsoDateString(start),
      shiftIndex,
      shiftDurationHours,
      engineHours,
      pauseHours: dto.totalPauseHours ?? 0,
      drilledMeters: dto.totalDrilledLength,
      rawDto: dto,
    };
  }

  /** Builds a BlastDrillingData from an already-loaded Tir object. */
  buildBlastDrillingData(
    tir: Tir,
    sessions: DrillSessionApiDto[] = [],
    holeCount = 0,
    targetMeters = 0,
    config: ShiftConfig = DEFAULT_SHIFT_CONFIG,
  ): BlastDrillingData {
    return {
      blastId: tir.tirId,
      label: tir.nom,
      zone: tir.nomZone ?? '',
      holeCount,
      targetMeters,
      blastDate: tir.dateTirPrevisionnelle
        ? this.toIsoDateString(tir.dateTirPrevisionnelle)
        : null,
      sessions: sessions.map(dto => this.mapApiDtoToSession(dto, config)),
    };
  }

  /**
   * Resolves the shift index (0-based) for a given local hour, based on the
   * ordered list of shift start hours.
   *
   * Each shift owns the window [shiftStartHours[i], shiftStartHours[i+1]).
   * The last shift wraps around midnight if its window crosses 00:00.
   *
   * Examples:
   *   shiftStartHours=[0,12],  hour=14 → 1 (night)
   *   shiftStartHours=[8,16,0], hour=6  → 2 (night, wraps midnight)
   *   shiftStartHours=[8,16,0], hour=10 → 0 (morning)
   */
  static resolveShiftIndex(localHour: number, shiftStartHours: number[]): number {
    const n = shiftStartHours.length;
    for (let i = 0; i < n; i++) {
      const start = shiftStartHours[i];
      const end   = shiftStartHours[(i + 1) % n];
      if (end > start) {
        // Normal window, e.g. [8, 16)
        if (localHour >= start && localHour < end) { return i; }
      } else {
        // Wrap-around window, e.g. [16, 8) spans midnight
        if (localHour >= start || localHour < end) { return i; }
      }
    }
    return 0;
  }

  private toIsoDateString(d: Date | string): string {
    const date = d instanceof Date ? d : new Date(d);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
